/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo } from 'react';

import { hashToHex } from 'js/lib/hash';
import { useLocation, useParams } from 'js/lib/useRouter';
import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import { breakpoints } from '@coursera/cds-core';

import { CoachChat } from 'bundles/ai-coach-platform/components/patterns';
import { useChatHistory } from 'bundles/ai-coach-platform/components/patterns/chat/ConversationHistory';
import { ChatEmptyState } from 'bundles/ai-coach-platform/components/patterns/chat/common';
import {
  clearChatSession,
  clearContinuousChatSession,
  dropMessages,
  sendMessage,
} from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import {
  getChatSessionStorageKey,
  getContinuousChatSessionStorageKey,
  persistentChatSessionId,
  persistentContinuousChatSessionId,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/sessionUtils';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';
import { firstNameFromFullName } from 'bundles/ai-course-coach/utils/nameParser';
import { PAGE_HEADER_Z_INDEX } from 'bundles/composable-page-header/constants';
import CourseraAiFooter from 'bundles/unified-common/components/coach-on-discovery/CourseraAiFooter';
import CourseraAiHeader from 'bundles/unified-common/components/coach-on-discovery/CourseraAiHeader';
import CourseraAiMessage from 'bundles/unified-common/components/coach-on-discovery/CourseraAiMessage';
import CourseraAiMessageLoader from 'bundles/unified-common/components/coach-on-discovery/CourseraAiMessageLoader';
import CourseraAiTemporaryStreamedMessage from 'bundles/unified-common/components/coach-on-discovery/CourseraAiTemporaryStreamedMessage';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import ChatZeroState from 'bundles/unified-description-page-common/components/coach/ChatZeroState';
import useSpecializationContextConfig from 'bundles/unified-description-page-common/components/coach/useSpecializationContextConfig';
import { XDP_CHAT_PANEL_WIDTH, XDP_GLOBAL_HEADER_HEIGHT } from 'bundles/unified-description-page-common/constants';

import _t from 'i18n!nls/xdp';

type Props = {
  /** True: chat aligns viewport-left with right border. False: viewport-right with left border. */
  chatOnLeft?: boolean;
};

const styles = {
  coachChat: css`
    padding: 0;

    .coach-message-client {
      display: flex;
      align-items: flex-end;
    }
  `,
  // Stable height for the chat's entire lifetime: top tracks the smart-header, bottom is pinned
  // to the viewport. CoachChat's flex layout never reflows during scroll.
  chatPanelWrapper: (chatOnLeft: boolean) => css`
    display: none;
    ${breakpoints.up('md')} {
      display: block;
      position: fixed;
      bottom: 0;
      overflow: hidden;
      z-index: ${PAGE_HEADER_Z_INDEX + 1};
      background: var(--cds-color-neutral-background-primary);
      width: ${XDP_CHAT_PANEL_WIDTH}px;
      padding: 0 var(--cds-spacing-600);
      ${chatOnLeft ? 'border-right' : 'border-left'}: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
};

const COACH_MODE_ID = 'XDP_CAREER_FIT_ASSISTANT_V3';
const COACH_WELCOME_ACTION_ID = 'WELCOME_MESSAGE';
const COACH_INTENTION_ROUTER_ACTION_ID = 'TYPED_MESSAGE';

const getChatSessionKey = (isAuthenticated: boolean, userId: number | undefined, productSlug: string) =>
  isAuthenticated && userId ? `logged-in-${hashToHex(String(userId))}-${productSlug}` : `logged-out-${productSlug}`;

const TwoPanelConversationalDiscoveryChatContainer = React.forwardRef<HTMLElement, Props>(
  ({ chatOnLeft = false }, ref) => {
    const { coachOpen, isContextConfigLoading } = useCoachState();
    const location = useLocation();
    const { productSlug } = useParams();
    const initialMessageSent = React.useRef(false);
    const { fullName: userName, id: userId } = getUser();
    const userFirstName = userName ? firstNameFromFullName(userName) : null;
    const displayTitle = userFirstName ? _t('Hi, #{userFirstName}!', { userFirstName }) : _t('Hi, learner!');
    const displaySubtitle = _t('How can I help?');
    const label = _t('Is this right for me?');
    const coachChatInputLabel = _t('Ask Coursera');
    const coachChatMessageListAriaLabel = _t('Ask Coursera chat messages');
    const coachChatContainerAriaLabel = _t('Ask Coursera chat container');
    const isAuthenticated = isAuthenticatedUser();

    // Fallback `top` for first paint; layout overwrites via the forwarded ref.
    const dockedInlineStyle: React.CSSProperties = {
      top: `${XDP_GLOBAL_HEADER_HEIGHT}px`,
      ...(chatOnLeft ? { left: 0 } : { right: 0 }),
    };

    const welcomeContextConfig = useSpecializationContextConfig({
      coachModeId: COACH_MODE_ID,
      coachActionId: COACH_WELCOME_ACTION_ID,
    });

    const routerContextConfig = useMemo(() => {
      if (!welcomeContextConfig) return undefined;
      return {
        ...welcomeContextConfig,
        config: {
          ...welcomeContextConfig.config,
          coachActionId: COACH_INTENTION_ROUTER_ACTION_ID,
        },
      };
    }, [welcomeContextConfig]);

    const sessionStorageKey = useMemo(() => {
      const chatSessionKey = getChatSessionKey(isAuthenticated, userId, productSlug!);
      return isAuthenticated
        ? getContinuousChatSessionStorageKey(location, undefined, chatSessionKey)
        : getChatSessionStorageKey(location, chatSessionKey);
    }, [location, productSlug, isAuthenticated, userId]);

    const sessionStorageId = useMemo(
      () =>
        isAuthenticated
          ? persistentContinuousChatSessionId.get(sessionStorageKey)
          : persistentChatSessionId.get(sessionStorageKey),
      [isAuthenticated, sessionStorageKey]
    );

    const { fetchChatHistory } = useChatHistory({
      historyLimit: 5,
      chatSessionId: sessionStorageId,
      mode: COACH_MODE_ID,
    });

    useEffect(() => {
      if (isContextConfigLoading || !welcomeContextConfig || initialMessageSent.current) {
        return;
      }
      initialMessageSent.current = true;

      const sendInitialMessage = () => {
        sendMessage({
          input: {
            message: label,
            ...welcomeContextConfig,
          },
          enableContinuousChat: true,
          enableTextStreaming: true,
        });
      };

      const resetStaleSession = () => {
        if (isAuthenticated) {
          persistentContinuousChatSessionId.clear(sessionStorageKey);
          clearContinuousChatSession();
        } else {
          persistentChatSessionId.clear(sessionStorageKey);
          clearChatSession();
        }
        dropMessages();
      };

      const restartSession = () => {
        resetStaleSession();
        sendInitialMessage();
      };

      const reportFetchHistoryError = (error: unknown) => {
        captureMessageInSentry({
          error: error instanceof Error ? error : new Error(String(error)),
          source: 'TwoPanelConversationalDiscoveryChatContainer.fetchChatHistory',
          coachMode: COACH_MODE_ID,
          coachAction: COACH_WELCOME_ACTION_ID,
          extra: { chatSessionId: sessionStorageId },
        });
      };

      const init = async () => {
        if (isAuthenticated) {
          if (!sessionStorageId) {
            sendInitialMessage();
            return;
          }
          try {
            const messages = await fetchChatHistory(sessionStorageId, true);
            if (messages.length === 0) {
              restartSession();
            }
          } catch (error) {
            reportFetchHistoryError(error);
            restartSession();
          }
        } else if (sessionStorageId) {
          // Logged-out users see a static welcome (ChatZeroState) instead of an LLM-generated one,
          // so we only need to clear stale sessions; the empty state handles the rest.
          try {
            const messages = await fetchChatHistory(sessionStorageId, true);
            if (messages.length === 0) {
              resetStaleSession();
            }
          } catch (error) {
            reportFetchHistoryError(error);
            resetStaleSession();
          }
        }
      };

      init();
    }, [
      isAuthenticated,
      isContextConfigLoading,
      welcomeContextConfig,
      label,
      sessionStorageId,
      sessionStorageKey,
      fetchChatHistory,
    ]);

    if (!coachOpen) return null;

    const chatContent =
      isContextConfigLoading || !routerContextConfig ? (
        <div>Loading...</div>
      ) : (
        <CoachChat
          chatSessionKey={getChatSessionKey(isAuthenticated, userId, productSlug!)}
          inputLabel={coachChatInputLabel}
          chatMessageListAriaLabel={coachChatMessageListAriaLabel}
          additionalStyles={styles.coachChat}
          showSettings={false}
          mode={COACH_MODE_ID}
          contextConfig={routerContextConfig}
          storeSessionId={true}
          emptyState={
            isAuthenticated ? (
              <ChatEmptyState title={displayTitle} subtitle={displaySubtitle} />
            ) : (
              <ChatZeroState contextConfig={routerContextConfig} />
            )
          }
          customHeader={<CourseraAiHeader />}
          renderCustomMessage={(props) => <CourseraAiMessage {...props} recommendedActionsLabel="" />}
          renderCustomMessageLoader={(props) => <CourseraAiMessageLoader {...props} />}
          renderCustomTemporaryStreamedMessage={(props) => <CourseraAiTemporaryStreamedMessage {...props} />}
          customFooter={<CourseraAiFooter />}
          enableTextStreaming={true}
          enableContinuousChat={isAuthenticated}
        />
      );

    return (
      <aside
        ref={ref}
        aria-label={coachChatContainerAriaLabel}
        css={styles.chatPanelWrapper(chatOnLeft)}
        style={dockedInlineStyle}
      >
        {chatContent}
      </aside>
    );
  }
);

// Memo isolates CoachChat from parent re-renders.
export default React.memo(TwoPanelConversationalDiscoveryChatContainer);
